// Turns live-update events into refetches. Targets are collected for a short random
// delay, so several events close together cause one refetch and open tabs don't all hit
// the server at the same instant. Each target can also have a minimum gap between runs:
// events inside the gap are held, and one trailing run happens once the gap ends, so
// nothing is lost but a continuous stream of events can't cause a refetch storm.

export const LIVE_REFRESH_MIN_DELAY_MS = 200;
export const LIVE_REFRESH_MAX_DELAY_MS = 1000;

/**
 * @param {object} options
 * @param {(targets: string[]) => boolean|void} options.run Runs the refetches; returns
 *   false when the page is paused (modal open, tab hidden, ...). The targets are then
 *   kept for `flush()`.
 * @param {Object<string, number>} [options.minGapMs] Minimum ms between runs, per target.
 * @param {Object<string, string[]>} [options.covers] Targets whose run also refreshes
 *   other targets (e.g. a full refetch that includes payment status).
 */
export const createLiveRefreshScheduler = ({
  run,
  minDelayMs = LIVE_REFRESH_MIN_DELAY_MS,
  maxDelayMs = LIVE_REFRESH_MAX_DELAY_MS,
  minGapMs = {},
  covers = {},
  now = Date.now,
  random = Math.random,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) => {
  const pending = new Set();
  const lastRunAt = new Map();
  let timer = null;
  let timerDueAt = Infinity;

  const waitFor = (target) => {
    const last = lastRunAt.get(target);
    if (last === undefined) return 0;
    return Math.max(0, last + (minGapMs[target] || 0) - now());
  };

  const jitter = () => minDelayMs + Math.floor(random() * Math.max(0, maxDelayMs - minDelayMs));

  const arm = (delay) => {
    const dueAt = now() + delay;
    if (timer && dueAt >= timerDueAt) return;
    if (timer) clearTimer(timer);
    timerDueAt = dueAt;
    timer = setTimer(fire, delay);
  };

  function fire() {
    timer = null;
    timerDueAt = Infinity;
    if (pending.size === 0) return;

    const ready = [...pending].filter((target) => waitFor(target) === 0);
    if (ready.length > 0) {
      ready.forEach((target) => pending.delete(target));
      if (run(ready) === false) {
        ready.forEach((target) => pending.add(target));
      } else {
        const ranAt = now();
        ready.forEach((target) => {
          lastRunAt.set(target, ranAt);
          (covers[target] || []).forEach((covered) => {
            lastRunAt.set(covered, ranAt);
            pending.delete(covered);
          });
        });
      }
    }

    // Targets still inside their gap get one trailing run when it ends.
    const waits = [...pending].map(waitFor).filter((wait) => wait > 0);
    if (waits.length > 0) arm(Math.min(...waits));
  }

  return {
    schedule(targets) {
      if (!targets?.length) return;
      targets.forEach((target) => pending.add(target));
      arm(Math.max(jitter(), Math.min(...targets.map(waitFor))));
    },
    /** Run anything kept back while paused (call when the page un-pauses). */
    flush() {
      if (timer || pending.size === 0) return;
      fire();
    },
    hasPending() {
      return pending.size > 0;
    },
    cancel() {
      if (timer) clearTimer(timer);
      timer = null;
      timerDueAt = Infinity;
      pending.clear();
    },
  };
};
